// Sidebar footer with user info and logout
// Created as part of ENG2-1699: Create AppLayout Component with Sidebar

import React from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOutAlt, faSun, faMoon } from '@fortawesome/free-solid-svg-icons'
import { tokens } from '../../styles/tokens'
import { SidebarFooterProps } from './sidebar.types'
import { useThemeMode } from '../../contexts/theme-context'

const SidebarFooter: React.FC<SidebarFooterProps> = ({ user, onLogout }) => {
  const { resolvedMode, toggleTheme } = useThemeMode()

  if (!user) {
    return null
  }

  return (
    <Box
      sx={{
        padding: '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 6px',
          borderRadius: `${tokens.radius.md}px`,
          transition: `background ${tokens.transitions.fast}`,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
          },
        }}
      >
        {/* User avatar */}
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            backgroundColor: tokens.colors.primary[500],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '13px',
            color: tokens.colors.grey[900],
            flexShrink: 0,
          }}
        >
          {user.initials}
        </Box>

        {/* User info */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#FFFFFF',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user.name}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.45)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user.email}
          </Typography>
        </Box>

        {/* Theme toggle button */}
        <Tooltip title={`Switch to ${resolvedMode === 'dark' ? 'light' : 'dark'} mode`}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.6)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <FontAwesomeIcon
              icon={resolvedMode === 'dark' ? faSun : faMoon}
              style={{ fontSize: '14px' }}
            />
          </IconButton>
        </Tooltip>

        {/* Logout button */}
        <Tooltip title="Sign out">
          <IconButton
            onClick={onLogout}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.6)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default SidebarFooter
